import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import "./budgets.css";

const emptyForecastForm = () => ({
  name: "",
  description: "",
  forecast_type: "rolling",
  start_date: "",
  end_date: "",
});

const emptyForecastVersionForm = () => ({
  version_name: "",
  method_default: "straight_line",
  actual_cutoff_date: "",
  notes: "",
});

function currency(n) {
  return `₦${Number(n || 0).toLocaleString()}`;
}

function monthShortLabel(dateStr) {
  const d = new Date(`${dateStr}T00:00:00`);
  return d.toLocaleString("en-US", { month: "short" });
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString();
}

function buildForecastMatrixRows(
  forecastLines = [],
  forecastMonths = {},
  drivers = [],
  formulas = []
) {
  const driverMap = new Map((drivers || []).map((d) => [d.id, d]));
  const formulaMap = new Map((formulas || []).map((f) => [f.id, f]));

  return forecastLines.map((line) => {
    const driver = line.driver_id ? driverMap.get(line.driver_id) : null;
    const formula = line.formula_id ? formulaMap.get(line.formula_id) : null;

    return {
      id: line.id,
      line,
      label: `${line.account?.account_number || ""} - ${
        line.account?.name || "Unknown"
      }`,
      subLabel: [
        line.forecast_method || null,
        driver ? `Driver: ${driver.name}` : null,
        formula ? `Formula: ${formula.name}` : null,
        line.department?.name || null,
        line.project?.name || null,
      ]
        .filter(Boolean)
        .join(" · "),
      group: line.account?.type || "Other",
      category: line.account?.category || "Other",
      months: (forecastMonths[line.id] || []).map((m) => ({
        ...m,
        month_key: m.month_start,
        month_label: m.month_label,
        actual_amount: Number(m.actual_amount) || 0,
        projected_amount: Number(m.projected_amount) || 0,
        override_amount:
          m.override_amount === null || m.override_amount === undefined
            ? null
            : Number(m.override_amount),
        final_amount: Number(m.final_amount) || 0,
      })),
    };
  });
}

function CreateForecastModal({
  open,
  onClose,
  onCreated,
  companyId,
  sessionUserId,
  selectedBudgetId,
}) {
  const [form, setForm] = useState(emptyForecastForm());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setForm((prev) => ({
        ...emptyForecastForm(),
        start_date: prev.start_date,
        end_date: prev.end_date,
      }));
      setErr("");
      setSaving(false);
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");

    try {
      setSaving(true);

      const { data, error } = await supabase
        .from("planning_forecasts")
        .insert({
          company_id: companyId,
          name: form.name.trim(),
          description: form.description.trim() || null,
          forecast_type: form.forecast_type,
          base_budget_id: selectedBudgetId || null,
          start_date: form.start_date,
          end_date: form.end_date,
          input_granularity: "monthly",
          status: "draft",
          created_by: sessionUserId,
          updated_by: sessionUserId,
        })
        .select()
        .single();

      if (error) throw error;

      await onCreated?.(data);
      onClose?.();
    } catch (error) {
      console.error("Create forecast error:", error);
      setErr(error.message || "Failed to create forecast.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="kp-modal-overlay" onClick={onClose}>
      <div
        className="kp-modal kp-modal-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="kp-modal-header">
          <div>
            <h3>Create Forecast</h3>
            <p>Create a new forecast workspace.</p>
          </div>
          <button
            type="button"
            className="kp-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form className="kp-form" onSubmit={handleSubmit}>
          <div className="kp-form-grid">
            <div className="kp-field">
              <label>Forecast Name</label>
              <input
                value={form.name}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, name: e.target.value }))
                }
                required
              />
            </div>

            <div className="kp-field">
              <label>Forecast Type</label>
              <select
                value={form.forecast_type}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    forecast_type: e.target.value,
                  }))
                }
              >
                <option value="rolling">rolling</option>
                <option value="static">static</option>
                <option value="reforecast">reforecast</option>
                <option value="cashflow">cashflow</option>
              </select>
            </div>

            <div className="kp-field">
              <label>Start Date</label>
              <input
                type="date"
                value={form.start_date}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    start_date: e.target.value,
                  }))
                }
                required
              />
            </div>

            <div className="kp-field">
              <label>End Date</label>
              <input
                type="date"
                value={form.end_date}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    end_date: e.target.value,
                  }))
                }
                required
              />
            </div>
          </div>

          <div className="kp-field">
            <label>Description</label>
            <textarea
              rows="3"
              value={form.description}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
            />
          </div>

          {err && <div className="kp-alert kp-alert-error">{err}</div>}

          <div className="kp-modal-footer">
            <button
              type="button"
              className="kp-btn kp-btn-soft"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="kp-btn kp-btn-primary"
              disabled={saving}
            >
              {saving ? "Creating..." : "Create Forecast"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditForecastLineModal({
  open,
  onClose,
  line,
  months,
  locked,
  onSaved,
  drivers,
  formulas,
}) {
  const [monthValues, setMonthValues] = useState([]);
  const [lineConfig, setLineConfig] = useState({
    forecast_method: "straight_line",
    driver_id: "",
    formula_id: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open && line) {
      setMonthValues(
        (months || []).map((m) => ({
          id: m.id,
          month_label: m.month_label,
          month_start: m.month_start,
          actual_amount: Number(m.actual_amount) || 0,
          projected_amount: Number(m.projected_amount) || 0,
          override_amount:
            m.override_amount === null || m.override_amount === undefined
              ? ""
              : Number(m.override_amount),
          final_amount: Number(m.final_amount) || 0,
        }))
      );

      setLineConfig({
        forecast_method: line.forecast_method || "straight_line",
        driver_id: line.driver_id || "",
        formula_id: line.formula_id || "",
        notes: line.notes || "",
      });

      setSaving(false);
      setErr("");
    }
  }, [open, line, months]);

  const saveLineConfigAndOverrides = async () => {
    setErr("");

    try {
      setSaving(true);

      const { error: lineErr } = await supabase
        .from("planning_forecast_lines")
        .update({
          forecast_method: lineConfig.forecast_method,
          driver_id: lineConfig.driver_id || null,
          formula_id: lineConfig.formula_id || null,
          notes: lineConfig.notes?.trim() || null,
          updated_by: (await supabase.auth.getUser()).data.user?.id || null,
        })
        .eq("id", line.id);

      if (lineErr) throw lineErr;

      for (const month of monthValues) {
        const overrideValue =
          month.override_amount === "" || month.override_amount === null
            ? null
            : Number(month.override_amount || 0);

        const { error } = await supabase.rpc("set_forecast_month_override", {
          p_forecast_month_id: month.id,
          p_override_amount: overrideValue,
        });

        if (error) throw error;
      }

      await onSaved?.();
      onClose?.();
    } catch (error) {
      console.error("Save forecast line config error:", error);
      setErr(error.message || "Failed to save forecast line settings.");
    } finally {
      setSaving(false);
    }
  };

  if (!open || !line) return null;

  return (
    <div className="kp-modal-overlay" onClick={onClose}>
      <div
        className="kp-modal kp-modal-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="kp-modal-header">
          <div>
            <h3>
              {line.account?.account_number} - {line.account?.name}
            </h3>
            <p>
              {line.department?.name || "No department"} ·{" "}
              {line.project?.name || "No project"}
            </p>
          </div>
          <button
            type="button"
            className="kp-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {locked ? (
          <div className="kp-note">
            This forecast version is locked. Reopen or work from a draft version
            before editing.
          </div>
        ) : (
          <>
            <div className="kp-form-grid kp-form-grid-4">
              <div className="kp-field">
                <label>Forecast Method</label>
                <select
                  value={lineConfig.forecast_method}
                  onChange={(e) =>
                    setLineConfig((prev) => ({
                      ...prev,
                      forecast_method: e.target.value,
                    }))
                  }
                >
                  <option value="straight_line">straight_line</option>
                  <option value="moving_average">moving_average</option>
                  <option value="growth_rate">growth_rate</option>
                  <option value="driver_based">driver_based</option>
                  <option value="formula">formula</option>
                  <option value="hybrid">hybrid</option>
                </select>
              </div>

              <div className="kp-field">
                <label>Driver</label>
                <select
                  value={lineConfig.driver_id}
                  onChange={(e) =>
                    setLineConfig((prev) => ({
                      ...prev,
                      driver_id: e.target.value,
                    }))
                  }
                >
                  <option value="">None</option>
                  {drivers.map((driver) => (
                    <option key={driver.id} value={driver.id}>
                      {driver.name} {driver.code ? `(${driver.code})` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="kp-field">
                <label>Formula</label>
                <select
                  value={lineConfig.formula_id}
                  onChange={(e) =>
                    setLineConfig((prev) => ({
                      ...prev,
                      formula_id: e.target.value,
                    }))
                  }
                >
                  <option value="">None</option>
                  {formulas.map((formula) => (
                    <option key={formula.id} value={formula.id}>
                      {formula.name} {formula.code ? `(${formula.code})` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="kp-field">
                <label>Notes</label>
                <input
                  value={lineConfig.notes}
                  onChange={(e) =>
                    setLineConfig((prev) => ({
                      ...prev,
                      notes: e.target.value,
                    }))
                  }
                  placeholder="Optional notes"
                />
              </div>
            </div>

            <div className="kp-month-grid">
              {monthValues.map((month, index) => (
                <div key={month.id} className="kp-month-box">
                  <label>{month.month_label}</label>
                  <input
                    type="number"
                    value={month.override_amount}
                    placeholder={`${month.final_amount}`}
                    onChange={(e) => {
                      const next = [...monthValues];
                      next[index] = {
                        ...next[index],
                        override_amount: e.target.value,
                      };
                      setMonthValues(next);
                    }}
                  />
                  <small>
                    Actual: {currency(month.actual_amount)}
                    <br />
                    Forecast: {currency(month.projected_amount)}
                    <br />
                    Final: {currency(month.final_amount)}
                  </small>
                </div>
              ))}
            </div>
          </>
        )}

        {err && <div className="kp-alert kp-alert-error">{err}</div>}

        <div className="kp-modal-footer">
          <button
            type="button"
            className="kp-btn kp-btn-soft"
            onClick={onClose}
            disabled={saving}
          >
            Close
          </button>
          {!locked && (
            <button
              type="button"
              className="kp-btn kp-btn-primary"
              onClick={saveLineConfigAndOverrides}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PlanningForecastMatrixTable({
  title,
  rows = [],
  collapsedGroups = {},
  onToggleGroup,
  onRowClick,
}) {
  const monthColumns = useMemo(() => {
    const months = Array.from(
      new Set(
        rows.flatMap((r) =>
          Array.isArray(r.months) ? r.months.map((m) => m.month_key) : []
        )
      )
    ).sort();

    return months.map((m) => ({
      key: m,
      label: monthShortLabel(m),
    }));
  }, [rows]);

  const grouped = useMemo(() => {
    const map = new Map();

    rows.forEach((row) => {
      const key = row.group || "Other";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(row);
    });

    return Array.from(map.entries()).map(([group, items]) => ({
      group,
      rows: items,
    }));
  }, [rows]);

  return (
    <div className="kp-card">
      <div className="kp-card-head">
        <h3>{title}</h3>
      </div>

      <div className="kp-table-wrap kp-planning-matrix-wrap">
        <table className="kp-table kp-planning-matrix">
          <thead>
            <tr>
              <th className="sticky-col first-col sticky-head">Line Item</th>
              {monthColumns.map((col) => (
                <th key={col.key} className="sticky-head">
                  {col.label}
                </th>
              ))}
              <th className="sticky-head">Year</th>
            </tr>
          </thead>

          <tbody>
            {grouped.length === 0 ? (
              <tr>
                <td colSpan={monthColumns.length + 2} className="kp-empty-cell">
                  No forecast lines yet.
                </td>
              </tr>
            ) : (
              grouped.map((section) => {
                const isCollapsed = !!collapsedGroups[section.group];

                const sectionTotals = {};
                monthColumns.forEach((col) => {
                  sectionTotals[col.key] = 0;
                });

                let sectionYear = 0;

                section.rows.forEach((row) => {
                  monthColumns.forEach((col) => {
                    const found = (row.months || []).find(
                      (m) => m.month_key === col.key
                    );
                    sectionTotals[col.key] += Number(found?.final_amount || 0);
                  });

                  sectionYear += (row.months || []).reduce(
                    (sum, m) => sum + (Number(m.final_amount) || 0),
                    0
                  );
                });

                return (
                  <React.Fragment key={section.group}>
                    <tr
                      className="kp-section-row kp-section-clickable"
                      onClick={() => onToggleGroup?.(section.group)}
                    >
                      <td
                        colSpan={monthColumns.length + 2}
                        className="sticky-col first-col"
                      >
                        <span className="kp-section-toggle">
                          {isCollapsed ? "▸" : "▾"}
                        </span>{" "}
                        {section.group}
                      </td>
                    </tr>

                    {!isCollapsed &&
                      section.rows.map((row) => {
                        const yearlyTotal = (row.months || []).reduce(
                          (sum, m) => sum + (Number(m.final_amount) || 0),
                          0
                        );

                        return (
                          <tr
                            key={row.id}
                            className="kp-clickable-row"
                            onClick={() => onRowClick?.(row)}
                          >
                            <td className="sticky-col first-col">
                              <div className="kp-line-main">
                                <strong>{row.label}</strong>
                                {row.subLabel ? (
                                  <small>{row.subLabel}</small>
                                ) : null}
                              </div>
                            </td>

                            {monthColumns.map((col) => {
                              const found = (row.months || []).find(
                                (m) => m.month_key === col.key
                              );

                              return (
                                <td key={col.key}>
                                  <div className="kp-scenario-cell">
                                    <div>
                                      A: {currency(found?.actual_amount || 0)}
                                    </div>
                                    <div>
                                      P:{" "}
                                      {currency(found?.projected_amount || 0)}
                                    </div>
                                    <div>
                                      F: {currency(found?.final_amount || 0)}
                                    </div>
                                  </div>
                                </td>
                              );
                            })}

                            <td>{currency(yearlyTotal)}</td>
                          </tr>
                        );
                      })}

                    {!isCollapsed && (
                      <tr className="kp-subtotal-row">
                        <td className="sticky-col first-col">Subtotal</td>
                        {monthColumns.map((col) => (
                          <td key={col.key}>
                            {currency(sectionTotals[col.key])}
                          </td>
                        ))}
                        <td>{currency(sectionYear)}</td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function ForecastBuilder({ selectedBudgetId = "" }) {
  const { companyId } = useCompany();

  const [sessionUserId, setSessionUserId] = useState("");
  const [role, setRole] = useState("");
  const [isAdmin] = useState(true);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState("");

  const [budgets, setBudgets] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [formulas, setFormulas] = useState([]);

  const [forecasts, setForecasts] = useState([]);
  const [selectedForecastId, setSelectedForecastId] = useState("");
  const [forecastVersions, setForecastVersions] = useState([]);
  const [selectedForecastVersionId, setSelectedForecastVersionId] =
    useState("");
  const [forecastLines, setForecastLines] = useState([]);
  const [forecastMonths, setForecastMonths] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [seedBudgetId, setSeedBudgetId] = useState(selectedBudgetId || "");

  const [versionForm, setVersionForm] = useState(emptyForecastVersionForm());

  const loadBudgets = useCallback(async () => {
    if (!companyId) return;

    const { data, error } = await supabase
      .from("budgets")
      .select("id, name, fiscal_year, status, start_date, end_date")
      .eq("company_id", companyId)
      .order("created_at", { ascending: false });

    if (error) throw error;

    setBudgets(data || []);
  }, [companyId]);

  const loadDriversAndFormulas = useCallback(async () => {
    if (!companyId) return;

    const [
      { data: driverRows, error: driverErr },
      { data: formulaRows, error: formulaErr },
    ] = await Promise.all([
      supabase
        .from("planning_drivers")
        .select("id, name, code, status")
        .eq("company_id", companyId)
        .order("name", { ascending: true }),
      supabase
        .from("planning_formulas")
        .select("id, name, code, status")
        .eq("company_id", companyId)
        .order("name", { ascending: true }),
    ]);

    if (driverErr) throw driverErr;
    if (formulaErr) throw formulaErr;

    setDrivers(driverRows || []);
    setFormulas(formulaRows || []);
  }, [companyId]);

  const forecastMatrixRows = useMemo(
    () =>
      buildForecastMatrixRows(forecastLines, forecastMonths, drivers, formulas),
    [forecastLines, forecastMonths, drivers, formulas]
  );

  const selectedForecast = useMemo(
    () => forecasts.find((f) => f.id === selectedForecastId) || null,
    [forecasts, selectedForecastId]
  );

  const selectedForecastVersion = useMemo(
    () =>
      forecastVersions.find((v) => v.id === selectedForecastVersionId) || null,
    [forecastVersions, selectedForecastVersionId]
  );

  useEffect(() => {
    if (selectedBudgetId) {
      setSeedBudgetId(selectedBudgetId);
      return;
    }

    if (!seedBudgetId && budgets.length > 0) {
      setSeedBudgetId(budgets[0].id);
    }
  }, [selectedBudgetId, seedBudgetId, budgets]);

  const loadSessionAndRole = useCallback(async () => {
    if (!companyId) return;

    const {
      data: { user },
      error: authErr,
    } = await supabase.auth.getUser();

    if (authErr) throw authErr;
    if (!user?.id) throw new Error("User session not found.");

    setSessionUserId(user.id);

    const { data: member, error: memberErr } = await supabase
      .from("company_members")
      .select("role, accepted")
      .eq("company_id", companyId)
      .eq("user_id", user.id)
      .maybeSingle();

    if (memberErr) throw memberErr;

    setRole(String(member?.role || "member").toLowerCase());
  }, [companyId]);

  const loadForecasts = useCallback(async () => {
    if (!companyId) return;

    const { data, error } = await supabase
      .from("planning_forecasts")
      .select("*")
      .eq("company_id", companyId)
      .order("created_at", { ascending: false });

    if (error) throw error;

    setForecasts(data || []);
    if (!selectedForecastId && data?.length) {
      setSelectedForecastId(data[0].id);
    }
  }, [companyId, selectedForecastId]);

  const loadVersions = useCallback(async () => {
    if (!selectedForecastId) {
      setForecastVersions([]);
      setSelectedForecastVersionId("");
      return;
    }

    const { data, error } = await supabase
      .from("planning_forecast_versions")
      .select("*")
      .eq("forecast_id", selectedForecastId)
      .order("created_at", { ascending: false });

    if (error) throw error;

    setForecastVersions(data || []);
    const current = (data || []).find((v) => v.is_current) || data?.[0];
    setSelectedForecastVersionId((prev) => prev || current?.id || "");
  }, [selectedForecastId]);

  const loadForecastVersionDetails = useCallback(async () => {
    if (!selectedForecastVersionId) {
      setForecastLines([]);
      setForecastMonths({});
      return;
    }

    const { data: lines, error: linesErr } = await supabase
      .from("planning_forecast_lines")
      .select(
        `
        *,
        account:chart_of_accounts(id, account_number, name, type, category),
        department:departments(id, name),
        project:projects(id, name)
      `
      )
      .eq("forecast_version_id", selectedForecastVersionId)
      .order("created_at", { ascending: true });

    if (linesErr) throw linesErr;

    setForecastLines(lines || []);

    if (!lines?.length) {
      setForecastMonths({});
      return;
    }

    const lineIds = lines.map((l) => l.id);

    const { data: months, error: monthsErr } = await supabase
      .from("planning_forecast_months")
      .select("*")
      .in("forecast_line_id", lineIds)
      .order("month_start", { ascending: true });

    if (monthsErr) throw monthsErr;

    const grouped = {};
    (months || []).forEach((month) => {
      if (!grouped[month.forecast_line_id]) {
        grouped[month.forecast_line_id] = [];
      }
      grouped[month.forecast_line_id].push(month);
    });

    setForecastMonths(grouped);
  }, [selectedForecastVersionId]);

  const initialize = useCallback(async () => {
    if (!companyId) return;

    try {
      setLoading(true);
      setErr("");
      await Promise.all([
        loadSessionAndRole(),
        loadForecasts(),
        loadBudgets(),
        loadDriversAndFormulas(),
      ]);
    } catch (error) {
      console.error("Forecast workspace load error:", error);
      setErr(error.message || "Failed to load forecasts.");
    } finally {
      setLoading(false);
    }
  }, [
    companyId,
    loadBudgets,
    loadDriversAndFormulas,
    loadForecasts,
    loadSessionAndRole,
  ]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    loadVersions().catch((error) => {
      console.error("Load forecast versions error:", error);
      setErr(error.message || "Failed to load forecast versions.");
    });
  }, [loadVersions]);

  useEffect(() => {
    loadForecastVersionDetails().catch((error) => {
      console.error("Load forecast details error:", error);
      setErr(error.message || "Failed to load forecast details.");
    });
  }, [loadForecastVersionDetails]);

  const refreshForecastDetails = async () => {
    setErr("");
    await loadForecastVersionDetails();
  };

  const handleCreated = async (forecastRow) => {
    await loadForecasts();
    if (forecastRow?.id) {
      setSelectedForecastId(forecastRow.id);
    }
  };

  const createForecastVersion = async (e) => {
    e.preventDefault();
    setErr("");
    setSuccess("");

    try {
      setSaving(true);

      const { data, error } = await supabase
        .from("planning_forecast_versions")
        .insert({
          company_id: companyId,
          forecast_id: selectedForecastId,
          version_name: versionForm.version_name.trim(),
          method_default: versionForm.method_default,
          status: "draft",
          is_current: false,
          is_locked: false,
          actual_cutoff_date: versionForm.actual_cutoff_date || null,
          notes: versionForm.notes.trim() || null,
          created_by: sessionUserId,
          updated_by: sessionUserId,
        })
        .select()
        .single();

      if (error) throw error;

      setVersionForm(emptyForecastVersionForm());
      setSuccess("Forecast version created successfully.");
      await loadVersions();
      setSelectedForecastVersionId(data.id);
    } catch (error) {
      console.error("Create forecast version error:", error);
      setErr(error.message || "Failed to create forecast version.");
    } finally {
      setSaving(false);
    }
  };

  const seedFromBudgetVersion = async () => {
    setErr("");
    setSuccess("");

    try {
      setSaving(true);

      if (!seedBudgetId) {
        throw new Error("Please select a budget to seed from.");
      }

      if (!selectedForecastVersionId) {
        throw new Error("Please select a forecast version first.");
      }

      const { data: budgetVersions, error: versionErr } = await supabase
        .from("budget_versions")
        .select("*")
        .eq("budget_id", seedBudgetId)
        .order("created_at", { ascending: false });

      if (versionErr) throw versionErr;

      const currentBudgetVersion =
        (budgetVersions || []).find((v) => v.is_current) || budgetVersions?.[0];

      if (!currentBudgetVersion?.id) {
        throw new Error("No budget version found to seed forecast from.");
      }

      const { error } = await supabase.rpc(
        "seed_forecast_lines_from_budget_version",
        {
          p_forecast_version_id: selectedForecastVersionId,
          p_budget_version_id: currentBudgetVersion.id,
          p_default_method: "straight_line",
        }
      );

      if (error) throw error;

      setSuccess("Forecast lines seeded from budget version.");
      await refreshForecastDetails();
    } catch (error) {
      console.error("Seed forecast from budget error:", error);
      setErr(error.message || "Failed to seed forecast from budget.");
    } finally {
      setSaving(false);
    }
  };

  const generateForecast = async () => {
    setErr("");
    setSuccess("");

    try {
      setSaving(true);

      const { error } = await supabase.rpc("generate_forecast_version_values", {
        p_forecast_version_id: selectedForecastVersionId,
        p_growth_rate: 5,
        p_moving_average_months: 3,
      });

      if (error) throw error;

      setSuccess("Forecast generated successfully.");
      await refreshForecastDetails();
    } catch (error) {
      console.error("Generate forecast error:", error);
      setErr(error.message || "Failed to generate forecast.");
    } finally {
      setSaving(false);
    }
  };

  const cloneForecastVersion = async () => {
    setErr("");
    setSuccess("");

    try {
      setSaving(true);

      if (!selectedForecastVersionId) {
        throw new Error("Select a forecast version first.");
      }

      const selectedVersion = forecastVersions.find(
        (v) => v.id === selectedForecastVersionId
      );
      if (!selectedVersion) {
        throw new Error("Selected forecast version was not found.");
      }

      const { data, error } = await supabase.rpc("clone_forecast_version", {
        p_source_forecast_version_id: selectedForecastVersionId,
        p_new_version_name: `${selectedVersion.version_name} Copy`,
        p_method_default: selectedVersion.method_default,
        p_notes: `Cloned from ${selectedVersion.version_name}`,
      });

      if (error) throw error;

      const row = Array.isArray(data) ? data[0] : data;

      setSuccess("Forecast version cloned successfully.");
      await loadVersions();

      if (row?.id) {
        setSelectedForecastVersionId(row.id);
      }
    } catch (error) {
      console.error("Clone forecast version error:", error);
      setErr(error.message || "Failed to clone forecast version.");
    } finally {
      setSaving(false);
    }
  };

  const toggleGroup = (groupName) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  const exportToExcel = () => {
    const monthKeys = Array.from(
      new Set(
        forecastMatrixRows.flatMap((row) =>
          (row.months || []).map((m) => m.month_key)
        )
      )
    ).sort();

    const rows = forecastMatrixRows.map((row) => {
      const item = {
        Line_Item: row.label,
        Details: row.subLabel || "",
        Group: row.group,
      };

      monthKeys.forEach((monthKey) => {
        const found = (row.months || []).find((m) => m.month_key === monthKey);
        item[`${monthShortLabel(monthKey)}_Actual`] = Number(
          found?.actual_amount || 0
        );
        item[`${monthShortLabel(monthKey)}_Projected`] = Number(
          found?.projected_amount || 0
        );
        item[`${monthShortLabel(monthKey)}_Final`] = Number(
          found?.final_amount || 0
        );
      });

      item.Year = (row.months || []).reduce(
        (sum, m) => sum + (Number(m.final_amount) || 0),
        0
      );

      return item;
    });

    const sheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Forecast Matrix");
    XLSX.writeFile(
      workbook,
      `${(selectedForecast?.name || "forecast").replace(
        /[^a-z0-9]/gi,
        "_"
      )}_matrix.xlsx`
    );
  };

  const exportToPDF = () => {
    const doc = new jsPDF("l", "mm", "a4");

    const monthKeys = Array.from(
      new Set(
        forecastMatrixRows.flatMap((row) =>
          (row.months || []).map((m) => m.month_key)
        )
      )
    ).sort();

    const head = [
      [
        "Line Item",
        ...monthKeys.map((monthKey) => monthShortLabel(monthKey)),
        "Year",
      ],
    ];

    const body = forecastMatrixRows.map((row) => {
      const yearTotal = (row.months || []).reduce(
        (sum, m) => sum + (Number(m.final_amount) || 0),
        0
      );

      return [
        row.label,
        ...monthKeys.map((monthKey) => {
          const found = (row.months || []).find(
            (m) => m.month_key === monthKey
          );
          return Number(found?.final_amount || 0).toLocaleString();
        }),
        yearTotal.toLocaleString(),
      ];
    });

    doc.setFontSize(14);
    doc.text(selectedForecast?.name || "Forecast Matrix", 14, 14);

    autoTable(doc, {
      head,
      body,
      startY: 20,
      styles: { fontSize: 7 },
      headStyles: { fillColor: [243, 244, 246], textColor: [17, 24, 39] },
      margin: { left: 8, right: 8 },
    });

    doc.save(
      `${(selectedForecast?.name || "forecast").replace(
        /[^a-z0-9]/gi,
        "_"
      )}_matrix.pdf`
    );
  };

  if (loading) {
    return <div className="kp-card">Loading forecasts…</div>;
  }

  const editingMonths =
    editingRow && editingRow.line
      ? forecastMonths[editingRow.line.id] || []
      : [];

  return (
    <div className="kp-budget-page">
      <div className="kp-budget-topbar">
        <div>
          <h2>Forecast Workspace</h2>
          <p>
            Forecast planning and review · Role:{" "}
            <strong>{role || "member"}</strong>
          </p>
        </div>

        <div className="kp-inline-actions">
          <button
            type="button"
            className="kp-btn kp-btn-soft"
            onClick={exportToExcel}
          >
            Export Excel
          </button>
          <button
            type="button"
            className="kp-btn kp-btn-soft"
            onClick={exportToPDF}
          >
            Export PDF
          </button>
          <button
            type="button"
            className="kp-btn kp-btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            Create Forecast
          </button>
        </div>
      </div>

      {err && <div className="kp-alert kp-alert-error">{err}</div>}
      {success && <div className="kp-alert kp-alert-success">{success}</div>}

      <div className="kp-grid-2">
        <div className="kp-card">
          <div className="kp-card-head">
            <h3>Forecasts</h3>
          </div>

          <div className="kp-budget-list">
            {forecasts.length === 0 ? (
              <div className="kp-empty">No forecasts yet.</div>
            ) : (
              forecasts.map((forecast) => (
                <button
                  key={forecast.id}
                  className={`kp-budget-item ${
                    selectedForecastId === forecast.id ? "selected" : ""
                  }`}
                  onClick={() => setSelectedForecastId(forecast.id)}
                >
                  <div className="kp-budget-item-top">
                    <strong>{forecast.name}</strong>
                    <span className="kp-badge status-draft">
                      {forecast.status || "draft"}
                    </span>
                  </div>
                  <div className="kp-budget-item-sub">
                    {forecast.forecast_type} · {formatDate(forecast.start_date)}{" "}
                    – {formatDate(forecast.end_date)}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        <div className="kp-card">
          <div className="kp-card-head">
            <h3>Create Forecast Version</h3>
          </div>

          {!selectedForecastId ? (
            <div className="kp-note">Select a forecast first.</div>
          ) : (
            <form className="kp-form" onSubmit={createForecastVersion}>
              <div className="kp-form-grid">
                <div className="kp-field">
                  <label>Version Name</label>
                  <input
                    value={versionForm.version_name}
                    onChange={(e) =>
                      setVersionForm((prev) => ({
                        ...prev,
                        version_name: e.target.value,
                      }))
                    }
                    required
                  />
                </div>

                <div className="kp-field">
                  <label>Method</label>
                  <select
                    value={versionForm.method_default}
                    onChange={(e) =>
                      setVersionForm((prev) => ({
                        ...prev,
                        method_default: e.target.value,
                      }))
                    }
                  >
                    <option value="straight_line">straight_line</option>
                    <option value="moving_average">moving_average</option>
                    <option value="growth_rate">growth_rate</option>
                    <option value="driver_based">driver_based</option>
                    <option value="formula">formula</option>
                    <option value="hybrid">hybrid</option>
                  </select>
                </div>

                <div className="kp-field">
                  <label>Actual Cutoff Date</label>
                  <input
                    type="date"
                    value={versionForm.actual_cutoff_date}
                    onChange={(e) =>
                      setVersionForm((prev) => ({
                        ...prev,
                        actual_cutoff_date: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              <div className="kp-field">
                <label>Notes</label>
                <textarea
                  rows="2"
                  value={versionForm.notes}
                  onChange={(e) =>
                    setVersionForm((prev) => ({
                      ...prev,
                      notes: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="kp-form-grid">
                <div className="kp-field">
                  <label>Seed Budget</label>
                  <select
                    value={seedBudgetId}
                    onChange={(e) => setSeedBudgetId(e.target.value)}
                  >
                    <option value="">Select budget</option>
                    {budgets.map((budget) => (
                      <option key={budget.id} value={budget.id}>
                        {budget.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="kp-inline-actions">
                <button className="kp-btn kp-btn-primary" disabled={saving}>
                  Add Forecast Version
                </button>

                {!!selectedForecastVersionId && (
                  <>
                    <button
                      type="button"
                      className="kp-btn kp-btn-soft"
                      onClick={seedFromBudgetVersion}
                      disabled={
                        saving || !seedBudgetId || !selectedForecastVersionId
                      }
                    >
                      Seed From Budget
                    </button>
                    <button
                      type="button"
                      className="kp-btn kp-btn-soft"
                      onClick={generateForecast}
                      disabled={saving || !selectedForecastVersionId}
                    >
                      Generate Forecast
                    </button>
                    <button
                      type="button"
                      className="kp-btn kp-btn-soft"
                      onClick={cloneForecastVersion}
                      disabled={saving || !selectedForecastVersionId}
                    >
                      Clone Version
                    </button>
                  </>
                )}
              </div>
            </form>
          )}
        </div>
      </div>

      <div className="kp-card">
        <div className="kp-card-head">
          <h3>Forecast Versions</h3>
        </div>

        <div className="kp-version-list">
          {forecastVersions.length === 0 ? (
            <div className="kp-empty">No forecast versions yet.</div>
          ) : (
            forecastVersions.map((version) => (
              <div
                key={version.id}
                className={`kp-version-item ${
                  selectedForecastVersionId === version.id ? "selected" : ""
                }`}
              >
                <button
                  className="kp-version-main"
                  onClick={() => setSelectedForecastVersionId(version.id)}
                >
                  <div>
                    <strong>{version.version_name}</strong>
                    <div className="kp-version-meta">
                      {version.method_default} · {version.status}
                      {version.is_current ? " · current" : ""}
                      {version.is_locked ? " · locked" : ""}
                      {version.actual_cutoff_date
                        ? ` · cutoff ${formatDate(version.actual_cutoff_date)}`
                        : ""}
                    </div>
                  </div>
                </button>

                <div className="kp-version-actions">
                  {isAdmin && !version.is_locked && (
                    <span className="kp-badge status-draft">editable</span>
                  )}
                  {version.is_locked && (
                    <span className="kp-badge status-archived">locked</span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <PlanningForecastMatrixTable
        title={
          selectedForecastVersion
            ? `Forecast Matrix · ${selectedForecastVersion.version_name}`
            : "Forecast Matrix"
        }
        rows={forecastMatrixRows}
        collapsedGroups={collapsedGroups}
        onToggleGroup={toggleGroup}
        onRowClick={(row) => setEditingRow(row)}
      />

      <div className="kp-note">
        Select a budget in the Seed Budget field, then click Seed From Budget to
        copy budget lines into the selected forecast version. Click any forecast
        line to set method, driver, formula, and overrides.
      </div>

      <CreateForecastModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handleCreated}
        companyId={companyId}
        sessionUserId={sessionUserId}
        selectedBudgetId={seedBudgetId}
      />

      <EditForecastLineModal
        open={!!editingRow}
        onClose={() => setEditingRow(null)}
        line={editingRow?.line || null}
        months={editingMonths}
        locked={!!selectedForecastVersion?.is_locked}
        onSaved={refreshForecastDetails}
        drivers={drivers}
        formulas={formulas}
      />
    </div>
  );
}
